import { useState, useEffect, useCallback } from "react";
import { useNavigate, useParams } from "react-router-dom";
import toast from "react-hot-toast";
import { parsePhoneNumberFromString } from "libphonenumber-js";
import { adminAPI } from "../services/admin.api";
import { isLikelyEmail, isValidPhone } from "../../auth/utils/validators";
import { useLanguage } from "../../../context/LanguageContext";
import type { GalleryItem } from "../components/AdminGallery";
import type {
  TalentAdmin,
  Client,
  EditUserFormState,
} from "../types";
import { ASSET_BASE_URL } from "../../../constants/env";

// ─── Helpers ──────────────────────────────────────────────────────────────────

const parseArray = (val: unknown): any[] => {
  if (Array.isArray(val)) return val;
  if (typeof val === "string") {
    try {
      const parsed = JSON.parse(val);
      return Array.isArray(parsed) ? parsed : [];
    } catch {
      return [];
    }
  }
  return [];
};

const toStorageUrl = (fp?: string | null): string => {
  if (!fp) return "";
  if (fp.startsWith("http")) return fp;
  return ASSET_BASE_URL + fp.replace(/^\/+/, "");
};

const toMediaItem = (m: any): GalleryItem => ({
  id: m.id,
  url: toStorageUrl(m.file_path ?? m.url ?? ""),
});

// ─── Constants ────────────────────────────────────────────────────────────────

// Local form state uses snake_case "code_name" for consistency with the rest
// of this file's field names; the backend's actual column/param is the
// camelCase "codeName" (translated at the load/save boundaries below).
const EMPTY_TALENT: TalentAdmin = {
  id: "",
  profile_id: "",
  categories: [],
  languages: [],
  skills: [],
  status: "pending",
  bio: "",
  code_name: "",
  date_of_birth: "",
  city: "",
  nationality: "",
  gender: "",
  talent_type: "",
  experience: "",
  rates_per: "",
  height: "",
  weight: "",
  eye_color: "",
  hair_color: "",
  skin_tone: "",
  hair_type: "",
  body_type: "",
  t_shirt_size: "",
  pant_size: "",
  shoe_size: "",
  custom_language: "",
  look_alike: "",
  specialties: [],
  specialty_options: [],
  profile_image: "",
  images: [],
} as any;

const EMPTY_FORM: EditUserFormState = {
  first_name: "",
  last_name: "",
  email: "",
  phone: "",
  phone_country: "",
  role: "client",
  profile_image: "",
  status: "pending",
  talent: { ...EMPTY_TALENT } as any,
  client: { id: "", company: "", company_type: "", title: "", whatsapp_number: "" },
};

// ─── Gallery loader ───────────────────────────────────────────────────────────

/**
 * Tries to resolve gallery media from multiple possible API response shapes.
 * Falls back to the talent detail endpoint if no media is found on the user object.
 */
async function loadGalleryMedia(user: any): Promise<GalleryItem[]> {
  // Prefer media embedded directly in the user response (various shapes)
  const embedded: any[] =
    user?.media ?? user?.user?.media ?? user?.talent?.media ?? [];

  if (embedded.length > 0) {
    return embedded.map(toMediaItem);
  }

  // Fallback: fetch via the talent detail endpoint
  const talentId = user?.talent?.id ?? user?.id;
  if (!talentId) return [];

  try {
    const { talentsAPI } = await import("../../../api/talentApi");
    const talentData = await talentsAPI.getById(String(talentId));
    const fallback: any[] = talentData?.user?.media ?? talentData?.media ?? [];
    return fallback.map(toMediaItem);
  } catch {
    return [];
  }
}

// ─── Hook ─────────────────────────────────────────────────────────────────────

export const useEditUser = () => {
  const { id } = useParams<{ id: string }>();
  const navigate = useNavigate();
  const { t } = useLanguage();

  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [form, setForm] = useState<EditUserFormState>(EMPTY_FORM);
  const [galleryMedia, setGalleryMedia] = useState<GalleryItem[]>([]);
  const [specialtiesOptions, setSpecialtiesOptions] = useState<
    { id: number; name: string; options?: { id: number; name: string }[] }[]
  >([]);

  useEffect(() => {
    import("../../../api/talentApi").then(({ talentsAPI }) =>
      talentsAPI
        .getSpecialties()
        .then((res: any) => setSpecialtiesOptions(res.data ?? []))
        .catch(() => {}),
    );
  }, []);

  // ── Load user ──────────────────────────────────────────────────────────────

  useEffect(() => {
    if (!id) {
      setLoading(false);
      return;
    }

    let cancelled = false;

    (async () => {
      try {
        const user = await adminAPI.getUser(id);
        if (cancelled) return;

        const t = user?.talent ?? {};
        const c = user?.client ?? {};

        const media = await loadGalleryMedia(user);
        if (!cancelled) setGalleryMedia(media);

        setForm({
          first_name: user.first_name ?? "",
          last_name: user.last_name ?? "",
          email: user.email ?? "",
          role: user.role ?? "client",
          phone: user.phone ?? "",
          phone_country: (user.phone && parsePhoneNumberFromString(user.phone)?.country) || "",
          status: user.status ?? "",
          profile_image: user.profile_image ?? "",
          talent: {
            ...EMPTY_TALENT,
            bio: t.bio ?? "",
            code_name: t.codeName ?? "",
            date_of_birth: t.date_of_birth?.split("T")[0] ?? "",
            city: t.city ?? "",
            nationality: t.nationality ?? "",
            gender: t.gender ?? "",
            languages: parseArray(t.languages),
            talent_type: t.talent_type ?? "",
            // FIX: API field is experience_years; map to local "experience" key.
            // Old hook read t.experience_years on load but wrote t.experience in save,
            // so a round-trip always lost the value.
            experience: t.experience_years ?? t.experience ?? "",
            rates_per: t.rates_per ?? "",
            height: t.height ?? "",
            weight: t.weight ?? "",
            eye_color: t.eye_color ?? "",
            hair_color: t.hair_color ?? "",
            skin_tone: t.skin_tone ?? "",
            hair_type: t.hair_type ?? "",
            body_type: t.body_type ?? "",
            // Backend column/rule name is hyphenated ("t-shirt_size"), which
            // isn't a valid JS identifier -- kept hyphen-free locally and
            // translated back on save, same treatment as codeName below.
            t_shirt_size: t["t-shirt_size"] ?? "",
            pant_size: t.pant_size ?? "",
            shoe_size: t.shoe_size ?? "",
            custom_language: t.custom_language ?? "",
            look_alike: t.look_alike ?? "",
            // Kept as the raw {id,name,options} shape returned by the API — the
            // existing read-only display block below relies on this shape.
            specialties: t.specialties ?? [],
            specialty_options: (t.specialty_options ?? t.specialtyOptions ?? []).map((o: any) => o.id),
          } as any,
          client: {
            id: c.id ?? "",
            title: c.title ?? "",
            company: c.company ?? "",
            company_type: c.company_type ?? "",
            whatsapp_number: c.whatsapp_number ?? "",
          },
        });
      } catch (err) {
        if (!cancelled) toast.error(t("toast.user.loadFailed"));
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    // Cleanup: if the component unmounts mid-fetch, ignore stale state updates
    return () => {
      cancelled = true;
    };
  }, [id]);

  // ── Field setters ──────────────────────────────────────────────────────────

  const setField = useCallback(
    (key: keyof EditUserFormState, value: any) =>
      setForm((prev) => ({ ...prev, [key]: value })),
    [],
  );

  const setTalentField = useCallback(
    (key: string, value: any) =>
      setForm((prev) => ({
        ...prev,
        talent: { ...(prev.talent as any), [key]: value },
      })),
    [],
  );

  const setClientField = useCallback(
    (key: keyof Client, value: any) =>
      setForm((prev) => ({
        ...prev,
        client: { ...prev.client, [key]: value },
      })),
    [],
  );

  // ── Save ───────────────────────────────────────────────────────────────────

  const save = useCallback(async () => {
    if (!id) return;

    if (form.email && !isLikelyEmail(form.email)) {
      toast.error(t("toast.user.invalidEmail"));
      return;
    }
    if (form.phone && !isValidPhone(form.phone)) {
      toast.error(t("toast.user.invalidPhone"));
      return;
    }
    const whatsapp = form.client?.whatsapp_number;
    if (whatsapp && !isValidPhone(whatsapp)) {
      toast.error(t("toast.user.invalidWhatsapp"));
      return;
    }

    setSaving(true);

    try {
      const payload: any = {
        first_name: form.first_name,
        last_name: form.last_name,
        email: form.email,
        phone: form.phone,
        phone_country: form.phone_country,
        role: form.role,
        status: form.status,
      };

      if (form.role === "talent") {
        const t = form.talent as any;
        payload.talent = {
          // The backend's Talent model column (and validation rule
          // talent.codeName) is camelCase -- must be sent as codeName, not
          // code_name, or Eloquent silently drops it as a non-fillable key.
          codeName: t.code_name ?? "",
          bio: t.bio,
          date_of_birth: t.date_of_birth || null,
          city: t.city,
          nationality: t.nationality,
          gender: t.gender,
          languages: t.languages ?? [],
          talent_type: t.talent_type,
          // FIX: always send as experience_years (API field name).
          // Old hook sent as t.experience which the backend didn't recognize.
          experience_years: Number(t.experience || 0),
          rates_per: t.rates_per,
          height: Number(t.height || 0) || null,
          weight: Number(t.weight || 0) || null,
          eye_color: t.eye_color,
          hair_color: t.hair_color,
          skin_tone: t.skin_tone,
          hair_type: t.hair_type,
          body_type: t.body_type,
          "t-shirt_size": t.t_shirt_size,
          pant_size: t.pant_size,
          shoe_size: t.shoe_size,
          custom_language: t.custom_language ?? "",
          look_alike: t.look_alike || null,
          specialty_options: t.specialty_options ?? [],
        };
      } else if (form.role === "client") {
        payload.client = form.client;
      }

      await adminAPI.updateUser(id, payload);
      toast.success(t("toast.user.updated"));
      navigate("/admin/users");
    } catch (err: any) {
      toast.error(err?.message || t("toast.user.saveFailed"));
    } finally {
      setSaving(false);
    }
  }, [id, form, navigate, t]);

  return {
    id,
    loading,
    saving,
    form,
    galleryMedia,
    specialtiesOptions,
    setField,
    setTalentField,
    setClientField,
    save,
  };
};
